import createAsyncGetters from '../config/asyncGetters/createAsyncGetters';
import { PetSpeciesID } from '../pet';
import { getPlayerProfile } from '../profile/profile.getters';
import { State } from '../State';
import { getPuzzleWinsLast7Days } from '../puzzleWinCount/puzzleWinCount.getters';
import {
  buddiesGetEventRewardDetails,
  buddiesGetExcludingIds,
} from './buddies.getters';
import {
  buddiesMaxParty,
  buddiesMaxPendingInvitesForSuggestions,
  buddiesMaxSuggestedPlayers,
  buddiesSchedule,
  buddiesUnlockLevel,
} from './buddies.ruleset';

export type BuddiesPlayer = {
  id: string;
  name: string;
  photo: string;
  species: PetSpeciesID;
};

export type BuddiesPlayerMap = Record<string, BuddiesPlayer>;

export type BuddiesSuggestedPlayer = {
  id: string;
  name: string;
  photo: string;
  species: PetSpeciesID;
  canReceiveInviteOA: boolean;
  hasPublicProfile: boolean;
};

function mapToSuggestedPlayer(
  state: State,
  player: {
    id: string;
    publicProfileName: string;
    publicProfilePhoto: string;
    petSpecies?: string;
    buddiesCanReceiveInviteOA: boolean;
    hasPublicProfile: boolean;
  },
): BuddiesSuggestedPlayer {
  const profile = getPlayerProfile(
    state,
    player.publicProfileName,
    player.publicProfilePhoto,
  );
  return {
    id: player.id,
    name: profile.name,
    photo: profile.photo,
    species: (player.petSpecies || 'ball') as PetSpeciesID,
    canReceiveInviteOA: player.buddiesCanReceiveInviteOA,
    hasPublicProfile: player.hasPublicProfile,
  };
}

export const buddiesAsyncGetters = createAsyncGetters({
  /**
   * Fetch player data for a list of player IDs.
   *
   * Queries the search index instead of `api.fetchStates`, which loads each player's
   * entire state - one queued DynamoDB read apiece, up to a full friend list at a
   * time - just to read three small fields.
   *
   * `name` is returned raw, so the caller applies the fallback for players without a
   * public profile - see `buddiesFetchPlayers`. That keeps the caller's own state out
   * of this getter, which otherwise had to load it in full for that one string.
   *
   * Note this reads the index rather than live state, so a player whose document is
   * stale - most often because they haven't played since `buddiesEnabled` shipped -
   * is left out until their next login or received message reindexes them.
   */
  buddiesGetPlayers: async (playerIds: string[], api) => {
    const playersMap: BuddiesPlayerMap = {};

    // At most one match per distinct id, and a search needs a limit of at least 1
    const ids = [...new Set(playerIds)];
    if (!ids.length) return playersMap;

    const search = await api.searchPlayers({
      where: {
        id: { isOneOf: ids },
        // Skip players the feature is not enabled for - currently guests - so that
        // they can't be pulled in through in-game friend invites.
        buddiesEnabled: { is: true },
        match2Level: { greaterThanOrEqual: buddiesUnlockLevel },
      },
      fields: ['publicProfileName', 'publicProfilePhoto', 'petSpecies'],
      limit: ids.length,
    });

    for (const player of search.results) {
      playersMap[player.id] = {
        id: player.id,
        name: player.publicProfileName,
        photo: player.publicProfilePhoto,
        species: (player.petSpecies || 'ball') as PetSpeciesID,
      };
    }

    return playersMap;
  },

  /**
   * Search for suggested non-friend players to invite to a Baking Buddies party.
   * Uses skill-based Gaussian scoring to find up to 5 compatible players.
   */
  buddiesGetSuggestedPlayers: async (_args: void, api) => {
    const state = await api.getOwnState();
    const now = api.date.now();

    // Can only search if there is an ongoing event
    const schedule = buddiesSchedule.getCurrentEvent(now);
    if (!schedule) return [];

    // Don't suggest players if the user's event is already over (grand prize reached or event expired)
    const rewardDetails = buddiesGetEventRewardDetails(state, schedule.id, now);
    if (rewardDetails?.isEventOver) return [];

    // Local dev fallback: plain search without scoring (Elasticsearch scoring not available)
    if (process.env.IS_LOCAL_DEVELOPMENT) {
      const unavailableIds = buddiesGetExcludingIds(state, now);
      const search = await api.searchPlayers({
        where: {
          hasPublicProfile: { is: true },
          id: { isNotOneOf: unavailableIds },
        },
        fields: [
          'publicProfileName',
          'publicProfilePhoto',
          'buddiesCanReceiveInviteOA',
          'hasPublicProfile',
          'petSpecies',
        ],
        limit: buddiesMaxSuggestedPlayers,
      });

      return search.results.map((player) =>
        mapToSuggestedPlayer(state, player),
      );
    }

    // User's number of wins in the last 7 days
    const userWinsLast7Days = getPuzzleWinsLast7Days(state, now).reduce(
      (acc, val) => acc + val,
      0,
    );

    // Minimum should be 1 for the % calculation. Prioritize players with 5% more wins.
    const baseUserWins = Math.max(1, userWinsLast7Days) * 1.05;

    // Get IDs to exclude (self, party, invites, rejected suggestions)
    const unavailableIds = buddiesGetExcludingIds(state, now);

    const suggestedPlayers = await api.searchPlayers({
      scoring: {
        functions: {
          buruFriendsCount: {
            // Prioritize players with 0 friends
            origin: 0,
            function: 'gauss',
            scale: 1,
            decay: 0.5,
          },
          puzzleWinsLast7Days: {
            origin: baseUserWins,
            function: 'gauss',
            scale: Math.ceil(baseUserWins * 0.1),
            decay: 0.5,
          },
          match2Level: {
            origin: state.match2.level,
            function: 'gauss',
            scale: 50,
            decay: 0.5,
          },
          // Prioritize players with smaller parties (more free slots)
          buddiesPartySize: {
            origin: 1,
            function: 'gauss',
            scale: 1,
            decay: 0.5,
          },
          // Prioritize players with fewer pending invites
          buddiesPendingInviteCount: {
            origin: 0,
            function: 'gauss',
            scale: 3,
            decay: 0.5,
          },
        },
        mode: 'multiply',
      },
      where: {
        match2Level: {
          greaterThanOrEqual: buddiesUnlockLevel,
        },
        buddiesEnabled: {
          is: true,
        },
        buddiesPendingInviteCount: {
          lessThan: buddiesMaxPendingInvitesForSuggestions,
        },
        buddiesPartySize: {
          lessThan: buddiesMaxParty,
        },
        hasPublicProfile: {
          is: true,
        },
        id: {
          isNotOneOf: unavailableIds,
        },
      },
      limit: buddiesMaxSuggestedPlayers,
      fields: [
        'publicProfileName',
        'publicProfilePhoto',
        'buddiesCanReceiveInviteOA',
        'hasPublicProfile',
        'petSpecies',
      ],
    });

    return suggestedPlayers.results.map((player) =>
      mapToSuggestedPlayer(state, player),
    );
  },
});
